import React from 'react';
import useIsPhone from '../hooks/useIsPhone';

const hostLabel = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return 'the live site';
    }
};

const ProjectViewer = ({ project, active, onActivate, onRelease }) => {
    const isPhone = useIsPhone();
    const isFrame = project.type === 'iframe';
    // Some sites cannot be browsed inside the frame at all (see openExternally),
    // so the call to action sends people to the real thing in a new tab.
    const externalOnly = Boolean(project.openExternally);
    // A few sites stop responding under any sandbox at all (LavenderBook's
    // buttons go dead even with every allow-* flag), so they opt out with
    // sandbox: false. React omits the attribute when it is undefined.
    const sandbox = project.sandbox === false
        ? undefined
        : "allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-downloads allow-top-navigation-by-user-activation";

    return (
        <div className={`project-viewer${active ? ' is-active' : ''}`}>
            <div className="viewer-frame">
                {isFrame && externalOnly && isPhone && project.mobilePreview ? (
                    // On phones these frames are only a backdrop for the "Open" button,
                    // and yellowducky.ca runs a frame-buster on load
                    // (preventPageLoadingInIframe) that some mobile browsers let
                    // through, sending the whole tab to the live site. A screenshot
                    // of the site's phone layout stands in for the frame instead.
                    <img
                        src={project.mobilePreview}
                        alt={`${project.title} on a phone`}
                        className="project-image viewer-mobile-preview"
                    />
                ) : isFrame ? (
                    // yellowducky.ca ships <base target="_top"> (Larapen master layout), so
                    // its links navigate the top-level context rather than this frame -
                    // clicking one leaves the portfolio for the live site, which is fine.
                    // allow-top-navigation-BY-USER-ACTIVATION rather than the unrestricted
                    // form: a real click can navigate away, but a script cannot bounce the
                    // tab on its own. Sites without <base target="_top"> still navigate
                    // inside the frame as normal.
                    <iframe
                        src={project.url}
                        title={project.title}
                        className="project-iframe"
                        sandbox={sandbox}
                        referrerPolicy="no-referrer-when-downgrade"
                        allow="fullscreen; clipboard-write"
                    />
                ) : project.type === 'image' ? (
                    <img
                        src={project.url}
                        alt={project.title}
                        className="project-image"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <div className="placeholder-content">
                        <h2>{project.title}</h2>
                        <p>Project content placeholder</p>
                    </div>
                )}
            </div>

            {/* Touch only: the frame covers most of a phone screen, so it stays a
                preview until tapped - otherwise every drag over it scrolls the
                embedded site and the page itself can never move. Both controls
                are display:none above 768px. */}
            {isFrame && !active && externalOnly && (
                <a
                    className="viewer-activate"
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${project.title} in a new tab`}
                >
                    <span className="viewer-activate-label">
                        Open {hostLabel(project.url)} &#8599;
                    </span>
                </a>
            )}

            {isFrame && !active && !externalOnly && (
                <button
                    type="button"
                    className="viewer-activate"
                    onClick={onActivate}
                    aria-label={`Interact with ${project.title}`}
                >
                    <span className="viewer-activate-label">Tap to browse this site</span>
                </button>
            )}

            {isFrame && active && (
                <button
                    type="button"
                    className="viewer-release"
                    onClick={onRelease}
                    aria-label="Stop interacting and scroll the page again"
                >
                    Done
                </button>
            )}
        </div>
    );
};

export default ProjectViewer;
